import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { LayoutDashboard, ClipboardList, Clock, FileText, UserCog, GraduationCap, Compass, ArrowRight } from 'lucide-react';
import PortalOverview from './PortalOverview';
import PortalHours from './PortalHours';
import PortalApplications from './PortalApplications';
import PortalDocuments from './PortalDocuments';
import PortalFieldInstructor from './PortalFieldInstructor';
import PortalPathway from './PortalPathway';

const TABS = [
  { value: 'overview', label: 'Overview', icon: LayoutDashboard },
  { value: 'pathway', label: 'My Pathway', icon: Compass },
  { value: 'applications', label: 'Applications', icon: ClipboardList },
  { value: 'hours', label: 'Hours & Logs', icon: Clock },
  { value: 'documents', label: 'Documents', icon: FileText },
  { value: 'instructor', label: 'Field Instructor', icon: UserCog },
];

const InternshipDashboard = ({ defaultTab = 'overview' }) => {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') || defaultTab);
  const [meta, setMeta] = useState({});
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => { document.title = 'My Internship Dashboard | I Need A Social Worker'; }, []);

  const loadMeta = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/internship-portal/meta`); setMeta(res.data || {}); }
    catch (e) { /* ignore */ }
  }, []);
  const loadPathwayStatus = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/internship-portal/pathway`); setNeedsSetup(!!res.data.needs_setup); }
    catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadMeta(); loadPathwayStatus(); }, [loadMeta, loadPathwayStatus]);

  const changeTab = (v) => { setTab(v); setParams(v === 'overview' ? {} : { tab: v }); };
  const onPathwaySaved = () => { loadPathwayStatus(); changeTab('overview'); };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
          <div>
            <p className="text-secondary font-semibold uppercase tracking-widest text-xs flex items-center gap-2"><GraduationCap className="w-4 h-4" /> Manage Internship Portal</p>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold text-primary mt-1" data-testid="dashboard-title">My Internship Dashboard</h1>
          </div>
          <Link to="/internships" className="text-primary hover:text-secondary text-sm font-medium">Browse internships →</Link>
        </div>

        <Tabs value={tab} onValueChange={changeTab}>
          <TabsList className="bg-white border rounded-xl p-1 flex flex-wrap h-auto gap-1 mb-6" data-testid="dashboard-tabs">
            {TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value} className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-lg px-4 py-2 flex items-center gap-2 relative" data-testid={`tab-${t.value}`}>
                <t.icon className="w-4 h-4" /> <span className="hidden sm:inline">{t.label}</span>
                {t.value === 'pathway' && needsSetup && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full" />}
              </TabsTrigger>
            ))}
          </TabsList>

          {needsSetup && tab !== 'pathway' && (
            <button
              onClick={() => changeTab('pathway')}
              className="w-full mb-6 text-left bg-secondary/10 border border-secondary/30 rounded-xl p-4 flex items-center justify-between gap-3 hover:bg-secondary/15 transition-colors"
              data-testid="complete-pathway-banner"
            >
              <div className="flex items-center gap-3">
                <Compass className="w-5 h-5 text-primary flex-shrink-0" />
                <div>
                  <p className="font-semibold text-primary">Complete Your Pathway</p>
                  <p className="text-sm text-gray-600">Set whether you're a student or completing supervised experience so we can load your official hour requirements. Your existing logs stay exactly as they are.</p>
                </div>
              </div>
              <span className="text-primary font-medium text-sm flex items-center gap-1 whitespace-nowrap">Set up <ArrowRight className="w-4 h-4" /></span>
            </button>
          )}

          <TabsContent value="overview"><PortalOverview onGoTab={changeTab} /></TabsContent>
          <TabsContent value="pathway"><PortalPathway onSaved={onPathwaySaved} /></TabsContent>
          <TabsContent value="applications"><PortalApplications meta={meta} /></TabsContent>
          <TabsContent value="hours"><PortalHours meta={meta} /></TabsContent>
          <TabsContent value="documents"><PortalDocuments meta={meta} /></TabsContent>
          <TabsContent value="instructor"><PortalFieldInstructor /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default InternshipDashboard;
